import { useEffect, useMemo, useState } from "react";
import { api, fmt } from "@/lib/api";
import Header from "@/components/site/Header";
import Ticker from "@/components/site/Ticker";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import ViralRadarPanel from "@/components/site/ViralRadarPanel";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const FREE_LIMIT = 5;
const PRO_KEY = "sa.clip_pro_token";

const PLATFORMS = [
  { id: "youtube", label: "YouTube" },
  { id: "youtube_shorts", label: "YouTube Shorts" },
  { id: "tiktok", label: "TikTok" },
  { id: "instagram", label: "Instagram Reels" },
];

const fmtDuration = (sec) => {
  if (!sec) return "-";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h) return `${h}h${String(m).padStart(2, "0")}`;
  return `${m}m${String(s).padStart(2, "0")}s`;
};

const THRESHOLDS = [
  { id: 100_000, label: "100k+" },
  { id: 300_000, label: "300k+" },
  { id: 1_000_000, label: "1M+" },
  { id: 10_000_000, label: "10M+" },
];

export default function ClipsPage() {
  const [platform, setPlatform] = useState("youtube");
  const [items, setItems] = useState([]);
  const [threshold, setThreshold] = useState(300_000);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [usingMock, setUsingMock] = useState(false);
  const [cachedAt, setCachedAt] = useState(null);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistSent, setWaitlistSent] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState("clip_pro_month");
  const [buyEmail, setBuyEmail] = useState("");
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    api.get("/clips/pro/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (platform !== "youtube" && platform !== "youtube_shorts") return;
    setLoading(true);
    const isShorts = platform === "youtube_shorts";
    const endpoint = isShorts ? "/viral/youtube-shorts" : "/clips";
    const params = new URLSearchParams({ min_views: String(threshold), limit: "60" });
    if (!isShorts && category !== "All") params.append("category", category);
    const token = localStorage.getItem(PRO_KEY);
    if (token) params.append("pro_token", token);
    api.get(`${endpoint}?${params.toString()}`)
      .then((r) => {
        setItems(r.data.clips || []);
        setUsingMock(!!r.data.using_mock);
        setCachedAt(r.data.cached_at);
        setIsPro(!!r.data.is_pro);
      })
      .finally(() => setLoading(false));
  }, [platform, threshold, category]);

  const categories = useMemo(() => {
    const set = new Set(["All"]);
    items.forEach((c) => c.category && set.add(c.category));
    return Array.from(set);
  }, [items]);

  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail) return;
    try {
      await api.post("/clips/waitlist", { email: waitlistEmail });
      setWaitlistSent(true);
      toast.success("You're on the list.", { description: "We'll email when Clip Radar Pro launches." });
    } catch (err) {
      toast.error("Couldn't join waitlist", { description: err?.response?.data?.detail || "Try again." });
    }
  };

  const buyPro = async (e) => {
    e.preventDefault();
    if (!buyEmail || !selectedPkg) return;
    setBuying(true);
    try {
      const { data } = await api.post("/clips/pro/checkout", {
        package_id: selectedPkg,
        origin_url: window.location.origin,
        email: buyEmail,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error("Checkout failed", { description: err?.response?.data?.detail || "Try again." });
      setBuying(false);
    }
  };

  const copyLink = (id) => {
    const url = `https://www.youtube.com/watch?v=${id}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Video URL copied", { description: url }),
      () => toast.error("Clipboard blocked")
    );
  };

  return (
    <div data-testid="clips-page" className="bg-white text-[#0a0a0a]">
      <Seo
        title="Clip Radar — the world's most-searched YouTube videos, ranked for clipping"
        description="Live radar of high-view YouTube videos with clip potential. Podcasts, streams, interviews — sorted by Clip Score (duration × recency × velocity × creator)."
      />
      <Header />
      <Ticker />

      {/* Hero */}
      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          <div className="overline text-[#52525b] mb-4">New · Freemium</div>
          <h1 className="font-display font-extrabold tracking-tighter leading-[0.95] text-5xl lg:text-7xl">
            Clip Radar.<br /><span className="text-[#ff3b30]">The videos worth clipping.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base lg:text-lg text-[#52525b]">
            Every 6 hours we scan YouTube for the videos most likely to earn from clips —
            ranked by our <b>Clip Score</b> (duration + recency + view velocity + creator&apos;s clip-friendliness).
            First 5 free, every day. No signup.
          </p>
          {usingMock && (
            <div className="mt-6 inline-block border border-black px-3 py-2 font-mono text-xs bg-[#f4f4f5]">
              ⚠ Demo mode — YouTube API key not configured. Showing sample data.
            </div>
          )}
        </div>
      </section>

      {/* Platform tabs */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-0 flex overflow-x-auto">
          {PLATFORMS.map((p) => (
            <button
              key={p.id}
              data-testid={`platform-tab-${p.id}`}
              onClick={() => setPlatform(p.id)}
              className={`overline py-4 px-5 border-r border-black transition-colors whitespace-nowrap ${
                platform === p.id
                  ? "bg-black text-white"
                  : "bg-white hover:bg-[#f4f4f5]"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </section>

      {(platform === "tiktok" || platform === "instagram") && (
        <ViralRadarPanel platform={platform} />
      )}

      {/* Filters */}
      {(platform === "youtube" || platform === "youtube_shorts") && (
      <section className="border-b border-black bg-[#f4f4f5]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-5 flex flex-wrap items-center gap-4">
          <span className="overline text-[#52525b]">Threshold</span>
          <div className="flex gap-2 flex-wrap">
            {THRESHOLDS.map((t) => (
              <button
                key={t.id}
                data-testid={`clips-threshold-${t.id}`}
                onClick={() => setThreshold(t.id)}
                className={`overline border border-black px-3 py-1.5 transition-colors ${
                  threshold === t.id ? "bg-black text-white" : "bg-white hover:bg-black hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <span className="overline text-[#52525b] ml-4">Category</span>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="clips-category" className="rounded-none border-black w-48 font-mono text-xs uppercase bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none border-black">
              {categories.map((c) => (
                <SelectItem key={c} value={c} className="rounded-none font-mono text-xs uppercase">{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto font-mono text-xs text-[#52525b] hidden md:block">
            {loading ? "loading…" : `${items.length} videos · cached ${cachedAt ? new Date(cachedAt).toLocaleString() : "n/a"}`}
          </div>
        </div>
      </section>
      )}

      {/* Grid */}
      {(platform === "youtube" || platform === "youtube_shorts") && (
      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8">
          {loading ? (
            <div className="py-20 text-center font-mono text-xs uppercase tracking-widest text-[#52525b]">
              Scanning YouTube…
            </div>
          ) : items.length === 0 ? (
            <div className="py-20 text-center font-mono text-xs uppercase tracking-widest text-[#52525b]">
              No clips match this filter yet. Try lowering the threshold.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-black">
              {items.map((c, i) => {
                const locked = !isPro && i >= FREE_LIMIT;
                return (
                  <article
                    key={c.video_id}
                    data-testid={`clip-card-${c.video_id}`}
                    className={`relative border-r border-b border-black p-4 ${locked ? "" : "hover:bg-[#fafafa]"} transition-colors`}
                  >
                    <div className={`aspect-video overflow-hidden border border-black mb-3 bg-black relative ${locked ? "blur-md" : ""}`}>
                      <img
                        src={c.thumbnail}
                        alt={c.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <span className="absolute bottom-2 right-2 bg-black text-white font-mono text-[10px] px-2 py-0.5">
                        {fmtDuration(c.duration_sec)}
                      </span>
                      {c.clip_friendly && !locked && (
                        <span className="absolute top-2 left-2 bg-[#ff3b30] text-white overline text-[10px] px-2 py-0.5">
                          Clip-friendly
                        </span>
                      )}
                    </div>
                    <div className={locked ? "blur-sm pointer-events-none select-none" : ""}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="overline text-[#52525b]">{c.category}</span>
                        <span className="font-mono text-xs">
                          Score <b className="text-[#ff3b30]">{c.clip_score}</b>
                        </span>
                      </div>
                      <h3 className="font-display font-extrabold text-base tracking-tight mt-1 line-clamp-2 min-h-[42px]">
                        {c.title}
                      </h3>
                      <div className="mt-2 font-mono text-xs text-[#52525b]">
                        {c.channel_title}
                      </div>
                      <div className="mt-3 flex justify-between font-mono text-xs">
                        <span>{fmt(c.views)} views · {c.days_old}d ago</span>
                        <span className="text-[#ff3b30]">{fmt(c.views_per_day)}/day</span>
                      </div>
                      <div className="mt-4 flex gap-2">
                        <a
                          href={`https://www.youtube.com/watch?v=${c.video_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-1 text-center overline border border-black bg-black text-white px-3 py-2 hover:bg-[#ff3b30]"
                        >
                          Watch →
                        </a>
                        <button
                          onClick={() => copyLink(c.video_id)}
                          className="overline border border-black px-3 py-2 hover:bg-black hover:text-white"
                        >
                          Copy URL
                        </button>
                      </div>
                    </div>

                    {locked && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/85 backdrop-blur-sm p-4 text-center">
                        <span className="overline text-[#ff3b30] mb-2">Pro</span>
                        <div className="font-display font-extrabold text-lg tracking-tight">Locked</div>
                        <div className="font-mono text-xs text-[#52525b] mt-1 mb-3">
                          Free tier: {FREE_LIMIT} clips/day.
                        </div>
                        <a
                          href="#waitlist"
                          className="overline border border-black bg-black text-white px-3 py-1.5 hover:bg-[#ff3b30]"
                        >
                          Unlock all →
                        </a>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
      )}

      {/* Waitlist / Pro */}
      <section id="waitlist" className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <div className="overline text-[#52525b] mb-4">{isPro ? "You're Pro ✓" : "Live · from $9 / month"}</div>
            <h2 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tighter leading-[0.95]">
              Clip Radar Pro.<br />
              <span className="text-[#ff3b30]">The full radar,<br />every 6 hours.</span>
            </h2>
            <ul className="mt-8 space-y-3 text-[15px]">
              <li className="flex gap-3"><span className="font-mono text-[#ff3b30]">✓</span>Unlimited clips (no daily cap)</li>
              <li className="flex gap-3"><span className="font-mono text-[#ff3b30]">✓</span>Full metadata: duration, velocity, days_old, region</li>
              <li className="flex gap-3"><span className="font-mono text-[#ff3b30]">✓</span>Custom view threshold + channel watchlist</li>
              <li className="flex gap-3"><span className="font-mono text-[#ff3b30]">✓</span>Priority alerts by email when a video crosses your threshold</li>
              <li className="flex gap-3"><span className="font-mono text-[#ff3b30]">✓</span>Export to CSV for your clipping team</li>
            </ul>
          </div>

          {isPro ? (
            <div className="border border-black p-8 lg:p-10 self-start bg-[#f4f4f5]">
              <div className="overline text-[#ff3b30] mb-3">Pro active</div>
              <h3 className="font-display font-extrabold text-3xl tracking-tight">You&apos;re unlocked.</h3>
              <p className="text-sm text-[#52525b] mt-2">Every clip is now revealed. Reload the page to see them all.</p>
            </div>
          ) : (
            <form onSubmit={buyPro} data-testid="clips-buy-form" className="border border-black p-8 lg:p-10 self-start">
              <div className="overline text-[#52525b] mb-4">Buy Pro (Stripe · secure)</div>
              <div className="space-y-2 mb-5">
                {packages.map((p) => (
                  <label
                    key={p.id}
                    className={`block border border-black p-4 cursor-pointer ${selectedPkg === p.id ? "bg-black text-white" : "bg-white hover:bg-[#fafafa]"}`}
                  >
                    <input
                      data-testid={`clips-pkg-${p.id}`}
                      type="radio"
                      name="pkg"
                      value={p.id}
                      checked={selectedPkg === p.id}
                      onChange={() => setSelectedPkg(p.id)}
                      className="mr-3"
                    />
                    <span className="overline mr-3">{p.label}</span>
                    <b className="float-right font-display text-xl">${p.amount.toFixed(0)}</b>
                  </label>
                ))}
              </div>
              <label className="overline block mb-2">Your email (for receipt + Pro access)</label>
              <input
                data-testid="clips-buy-email"
                type="email"
                required
                value={buyEmail}
                onChange={(e) => setBuyEmail(e.target.value)}
                placeholder="you@studio.com"
                className="w-full border border-black px-4 py-3 font-mono text-sm focus:outline-none"
              />
              <button
                data-testid="clips-buy-submit"
                type="submit"
                disabled={buying}
                className="w-full mt-4 bg-black text-white py-3 overline hover:bg-[#ff3b30] disabled:opacity-60"
              >
                {buying ? "Redirecting to Stripe…" : "Unlock Pro →"}
              </button>
              <p className="font-mono text-[10px] text-[#52525b] mt-3 text-center">
                Secure payment via Stripe · instant access
              </p>
              <div className="mt-6 pt-6 border-t border-black/15">
                <div className="overline text-[#52525b] mb-2">Or just get notified</div>
                <div className="flex border border-black bg-white">
                  <input
                    data-testid="clips-waitlist-email"
                    type="email"
                    disabled={waitlistSent}
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    placeholder="you@studio.com"
                    className="flex-1 px-3 py-2 font-mono text-xs focus:outline-none disabled:bg-[#f4f4f5]"
                  />
                  <button
                    data-testid="clips-waitlist-submit"
                    type="button"
                    disabled={waitlistSent}
                    onClick={submitWaitlist}
                    className="bg-white text-black px-4 overline hover:bg-black hover:text-white border-l border-black disabled:opacity-60"
                  >
                    {waitlistSent ? "✓" : "Join list"}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
